import { useState, useEffect, useCallback, useRef } from 'react';
import { useInvalidateRelease } from '@/lib/invalidateRelease';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ICONS } from '@/constants/icons';
import { useTeamVariations } from '@/hooks/useVariations';
import { VariationsTable } from '@/components/variations/VariationsTable';
import { VariationFiltersIndicator } from '@/components/variations/VariationFiltersIndicator';
import { useVariationsFilterStore } from '@/stores/variationsFilterStore';
import { VariationFilterDrawer } from '@/components/variations/VariationFilterDrawer';
import { VariationCreator } from '@/components/VariationCreator';
import { BulkImportDialog } from '@/components/bulk/BulkImportDialog';
import { PostCreator } from '@/components/PostCreator';
import { ConfirmDialogSimple } from '@/components/ui/ConfirmDialog';
import { MappingRulesModal } from '@/components/calendar/MappingRulesModal';
import { VariationSettingsModal } from '@/components/variations/VariationSettingsModal';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { CancelButton } from '@/components/ui/CancelButton';
import { useCalendars } from '@/hooks/useCalendar';
import { PostVariation } from '@/types/variations';
import { variationsService } from '@/services/variations.service';
import { toast } from '@/utils/toast';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { usePostPermissions, variationToReleaseFacts } from '@/hooks/usePostPermissions';
import { approvalService } from '@/services/approval.service';
import { announceReleaseMoment } from '@/stores/releaseMoment.store';
import { formatApiError } from '@/services/api';
import { isSupportedLanguage } from '@/utils/languages';

/**
 * Variations Page
 *
 * Lists all variations across all posts for the team with:
 * - Search and filter functionality
 * - Table view with variation details
 * - Actions: Edit, Delete, Duplicate
 * - Opens VariationCreator for editing
 */

// A language variation is one with an explicit SUPPORTED language (never guessed from its title); an unknown stored
// code (e.g. "xx") gets no Language badge but stays intact in the data and edit form.
const getVariationType = (variation: Pick<PostVariation, 'language'>): 'Language' | 'Custom' => {
  return isSupportedLanguage(variation.language) ? 'Language' : 'Custom';
};

export const Variations = () => {
  const { t } = useTranslation('common');
  const invalidateRelease = useInvalidateRelease();
  const { isAgencyTeam, carteBlanche, canManageBrandSettings, canCreateVariation } = useReviewerRestrictions();
  const workflowEnabled = isAgencyTeam && !carteBlanche;
  const permissions = usePostPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // Modal state
  const [isCreatorOpen, setIsCreatorOpen] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState<PostVariation | null>(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Filter state
  const { filters, setFilter: handleFilterChange, clearFilters: handleClearFilters } = useVariationsFilterStore();
  const {
    search: searchQuery,
    type: typeFilter,
    status: statusFilter,
    language: languageFilter,
    workflowStatus: workflowStatusFilter,
    labels: labelsFilter,
  } = filters;

  // Selection state
  const [selectedVariations, setSelectedVariations] = useState<number[]>([]);

  // Post editor state
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [showPostCreator, setShowPostCreator] = useState(false);

  // Bulk action state
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Split button dropdown
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);

  // Detect when toolbar becomes stuck
  const toolbarSentinelRef = useRef<HTMLDivElement>(null);
  const [, setIsToolbarStuck] = useState(false);

  useEffect(() => {
    const sentinel = toolbarSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsToolbarStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Variation settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Mapping rules state
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState<number | null>(null);
  const [mappingRulesOpen, setMappingRulesOpen] = useState(false);

  const { data: calendars } = useCalendars({ isActive: true });

  // Deep link `?post=<postId>&variation=<variationId>` (Release drawer "Edit this version"): open the variation
  // editor on exactly that version. The editor applies the release lock itself (In review / Released = read-only
  // with the lock banner), so the link is safe to follow from any state. Params are consumed at once so a
  // reload or back-navigation does not re-open it; a version that cannot be loaded (deleted, other brand, no
  // access) gets one clear message instead of a silent empty page.
  useEffect(() => {
    const postParam = searchParams.get('post');
    const variationParam = searchParams.get('variation');
    if (postParam === null && variationParam === null) return;
    const next = new URLSearchParams(searchParams);
    next.delete('post');
    next.delete('variation');
    setSearchParams(next, { replace: true });
    if (!/^\d+$/.test(postParam ?? '') || !/^\d+$/.test(variationParam ?? '')) return;
    variationsService
      .getForEditor(Number(postParam), Number(variationParam))
      .then((target) => {
        setSelectedVariation(target);
        setIsCreatorOpen(true);
      })
      .catch(() => toast.error(t('variation_link_not_found')));
  }, [searchParams, setSearchParams, t]);

  /**
   * Fetch all variations for the team using the backend API
   * The API handles filtering by type, contentStatus, and search
   */
  const { data: variationsResponse, isLoading, error, refetch } = useTeamVariations({
    type: typeFilter !== 'all' ? (typeFilter as 'language' | 'custom') : undefined,
    contentStatus: statusFilter !== 'all' ? (statusFilter as 'DRAFT' | 'READY') : undefined,
    search: searchQuery || undefined,
    language: languageFilter !== 'all' ? languageFilter : undefined,
    labels: labelsFilter || undefined,
    workflowStatus: workflowStatusFilter !== 'all' ? (workflowStatusFilter as 'PENDING' | 'APPROVED' | 'CHANGES_REQUESTED') : undefined,
  });

  // Extract variations array from response
  const allVariations = variationsResponse?.data ?? [];

  /**
   * API already handles filtering, so we can use the data directly
   */
  const filteredVariations = allVariations;

  /**
   * Handle variation refresh after edits
   */
  const handleRefresh = () => {
    refetch();
  };

  /**
   * Handle create variation - opens modal in create mode
   */
  const handleCreateVariation = () => {
    setSelectedVariation(null);
    setIsCreatorOpen(true);
  };

  /**
   * Handle edit original post - opens PostCreator in edit mode
   */
  const handleEditPost = (postId: number) => {
    setEditingPostId(postId);
    setShowPostCreator(true);
  };

  const handleClosePostCreator = () => {
    setShowPostCreator(false);
    setEditingPostId(null);
  };

  /**
   * Handle edit variation - opens modal in edit mode
   */
  const handleEditVariation = (variation: PostVariation) => {
    setSelectedVariation(variation);
    setIsCreatorOpen(true);
  };

  /**
   * Handle close creator modal
   */
  const handleCloseCreator = () => {
    setIsCreatorOpen(false);
    setSelectedVariation(null);
  };

  /**
   * Handle successful create/edit - refresh list and close modal
   */
  const handleSuccess = () => {
    invalidateRelease();
    refetch();
    handleCloseCreator();
  };

  /**
   * Can the current user edit / delete a given variation? Judged on the variation's OWN release record and
   * occurrences (never the parent post's), by the release rulebook mirror in usePostPermissions.
   */
  const { canEditItem, canDeleteItem } = permissions;
  const canEditVariation = useCallback(
    (variation: PostVariation): boolean => canEditItem(variationToReleaseFacts(variation)),
    [canEditItem]
  );
  const canDeleteVariation = useCallback(
    (variation: PostVariation): boolean =>
      // A post's only variation can't be deleted (server refuses with 400).
      (variation.post?.variationCount ?? 2) > 1 && canDeleteItem(variationToReleaseFacts(variation)),
    [canDeleteItem]
  );

  /**
   * Handle select variation
   */
  const handleSelectVariation = (variationId: number) => {
    setSelectedVariations((prev) =>
      prev.includes(variationId) ? prev.filter((id) => id !== variationId) : [...prev, variationId]
    );
  };

  /**
   * Handle select all variations
   */
  const handleSelectAll = () => {
    if (selectedVariations.length === filteredVariations.length) {
      setSelectedVariations([]);
    } else {
      setSelectedVariations(filteredVariations.map((v) => v.id));
    }
  };

  /**
   * Clear selection
   */
  const clearSelection = () => setSelectedVariations([]);

  /**
   * Submit variation for approval mutation
   */
  const submitForApprovalMutation = useMutation({
    mutationFn: ({ postId, variationId }: { postId: number; variationId: number }) =>
      approvalService.submitVariationForApproval(postId, variationId),
    onSuccess: () => {
      announceReleaseMoment('sent');
      invalidateRelease();
      refetch();
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });

  /**
   * Handle send for review (single variation)
   */
  const handleSendForReview = async (variation: PostVariation) => {
    try {
      await submitForApprovalMutation.mutateAsync({
        postId: variation.postId,
        variationId: variation.id,
      });
    } catch {
      // Error handled by mutation onError
    }
  };

  /**
   * Handle bulk send for review
   */
  const handleBulkSendForReview = async () => {
    setBulkActionLoading(true);
    try {
      const selected = filteredVariations.filter((v) => selectedVariations.includes(v.id));
      // Only send variations that aren't already pending or approved
      const eligible = selected.filter(
        (v) => v.workflowStatus !== 'PENDING' && v.workflowStatus !== 'APPROVED'
      );

      if (eligible.length === 0) {
        toast.info(t('no_eligible_variations', { defaultValue: 'No eligible variations to send for review' }));
        setBulkActionLoading(false);
        return;
      }

      const outcomes = await Promise.allSettled(
        eligible.map((v) => approvalService.submitVariationForApproval(v.postId, v.id))
      );
      const sent = outcomes.filter((outcome) => outcome.status === 'fulfilled').length;
      const failedIds = eligible.filter((_, index) => outcomes[index].status === 'rejected').map((v) => v.id);
      announceReleaseMoment('sent', sent, failedIds.length === 0);
      setSelectedVariations(failedIds);
      invalidateRelease();
      refetch();
      const firstFailure = outcomes.find((outcome) => outcome.status === 'rejected');
      if (firstFailure?.status === 'rejected') toast.error(formatApiError(firstFailure.reason).message);
    } catch (error) {
      toast.error(formatApiError(error).message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  /**
   * Handle bulk delete
   *
   * Only deletes variations the current user is permitted to delete.
   * Variations blocked by the approval workflow (e.g. APPROVED status when
   * the user is a client) are silently skipped.
   */
  const handleBulkDelete = async () => {
    setBulkActionLoading(true);
    try {
      const selected = filteredVariations.filter((v) => selectedVariations.includes(v.id));
      // Respect per-variation delete permissions (workflow-aware).
      // Also never leave a post without variations: when every variation of a post is selected, keep one.
      const permitted = selected.filter((v) => canDeleteVariation(v));
      const keptPerPost = new Set<number>();
      const eligible = permitted.filter((v) => {
        const total = v.post?.variationCount ?? 2;
        const chosen = permitted.filter((o) => o.postId === v.postId);
        if (chosen.length < total) return true;
        // all variations of this post selected: spare the first one
        if (!keptPerPost.has(v.postId)) {
          keptPerPost.add(v.postId);
          return false;
        }
        return true;
      });
      if (eligible.length === 0) {
        toast.info(t('no_eligible_variations_delete', { defaultValue: 'No variations can be deleted with your current permissions' }));
        setBulkActionLoading(false);
        return;
      }
      await Promise.all(eligible.map((v) => variationsService.delete(v.postId, v.id)));
      toast.success(
        t('variations_deleted', {
          count: eligible.length,
          defaultValue: `${eligible.length} variations deleted`,
        })
      );
      clearSelection();
      invalidateRelease();
      refetch();
    } catch (error) {
      // Some deletes may have succeeded before the failure: resync everywhere
      invalidateRelease();
      toast.error(t('bulk_delete_error', { defaultValue: 'Failed to delete some variations' }));
    } finally {
      setBulkActionLoading(false);
    }
  };

  /**
   * Clear selection when filters change
   */
  useEffect(() => {
    setSelectedVariations([]);
  }, [searchQuery, typeFilter, statusFilter, languageFilter, workflowStatusFilter, labelsFilter]);

  // Close create menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target as Node)) {
        setCreateMenuOpen(false);
      }
    };
    if (createMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [createMenuOpen]);

  const handleOpenMappingRules = () => {
    setCreateMenuOpen(false);
    setShowCalendarPicker(true);
  };

  const handleCalendarSelected = () => {
    if (!selectedCalendarId) return;
    setShowCalendarPicker(false);
    setMappingRulesOpen(true);
  };

  return (
    <div>
      {/* Page Header */}
      <PageHeader
        title={
          <>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <span className="text-bs-secondary">V</span>ariations
          </>
        }
        subtitle={t('variations_subtitle')}
        icon={ICONS.LANGUAGE}
        headerType="list"
        documentTitle="Variations"
      />

      {/* Main content */}
      <div className="space-y-4">
        {/* Sentinel to detect when toolbar becomes sticky */}
        <div ref={toolbarSentinelRef} className="h-0" aria-hidden="true" />
        {/* Toolbar */}
        <div className="flex items-center gap-1.5 sm:gap-2 sticky top-navbar-height z-30 -mx-6 px-6 py-2 sm:py-3 bg-gray-50">
          {/* Filter Button */}
          <Button
            variant="outline"
            onClick={() => setIsFilterDrawerOpen(true)}
            className="flex items-center gap-2 !h-9 sm:!h-10"
          >
            <Icon name={ICONS.FILTER} size="sm" ariaHidden />
            <span className="hidden sm:inline">{t('filters', { defaultValue: 'Filters' })}</span>
          </Button>

          {/* Search */}
          <div className="flex-1 min-w-0 lg:flex-none lg:w-64">
            <Input
              type="search"
              placeholder={t('search_variations')}
              value={searchQuery}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full !h-9 sm:!h-10 !py-0"
            />
          </div>

          {/* Spacer — desktop only */}
          <div className="hidden lg:block flex-1" />

          {/* Create Variation Split Button (creators only) */}
          {canCreateVariation && (
          <div className="relative inline-flex gap-1.5 sm:gap-2" ref={createMenuRef}>
            <Button
              variant="primary"
              onClick={handleCreateVariation}
              className="flex items-center gap-2 h-[clamp(32px,4vh,38px)]"
            >
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <span className="material-symbols-outlined leading-none" style={{ fontSize: 'clamp(14px, 2vh, 18px)' }}>edit_square</span>
              <span className="hidden sm:inline">{t('create_variation', { defaultValue: 'Create Variation' })}</span>
            </Button>
            <Button
              variant="primary"
              onClick={() => setCreateMenuOpen(!createMenuOpen)}
              className="!px-2.5 h-[clamp(32px,4vh,38px)]"
              aria-label={t('more_options', { defaultValue: 'More options' })}
              aria-expanded={createMenuOpen}
            >
              <Icon name={ICONS.STAR} size="sm" ariaHidden />
            </Button>

            {createMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-30">
                <button
                  onClick={() => {
                    setBulkImportOpen(true);
                    setCreateMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-gray-700 hover:bg-gray-100"
                >
                  <Icon name={ICONS.UPLOAD} size="sm" ariaHidden />
                  {t('import_variations', { defaultValue: 'Import Variations' })}
                </button>
                {canManageBrandSettings && (
                <button
                  onClick={handleOpenMappingRules}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-gray-700 hover:bg-gray-100"
                >
                  <Icon name={ICONS.LINK} size="sm" ariaHidden />
                  {t('mapping_rules', { defaultValue: 'Mapping Rules' })}
                </button>
                )}
                {canManageBrandSettings && (
                <button
                  onClick={() => {
                    setSettingsOpen(true);
                    setCreateMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-gray-700 hover:bg-gray-100"
                >
                  <Icon name={ICONS.SETTINGS} size="sm" ariaHidden />
                  {t('variation_settings', { defaultValue: 'Variation Settings' })}
                </button>
                )}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Active Filters Indicator (same bar as Creator) */}
        <VariationFiltersIndicator />

        {/* Bulk Action Bar */}
        {selectedVariations.length > 0 && (
          <div className="sticky top-[calc(4.1rem+59px)] z-20 flex flex-col gap-2 p-3 sm:p-4 bg-soft-mint border border-light-teal-gray rounded-lg shadow-md">
            {/* Row 1: Count + close button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-dark-teal" ariaHidden />
                <span className="text-sm font-medium text-dark-teal">
                  {t(
                    selectedVariations.length === 1 ? 'variation_selected' : 'variations_selected',
                    {
                      count: selectedVariations.length,
                      defaultValue: `${selectedVariations.length} selected`,
                    }
                  )}
                </span>
              </div>
              <button
                onClick={clearSelection}
                disabled={bulkActionLoading}
                className="p-1.5 rounded-lg hover:bg-light-teal-gray/50 transition-colors text-dark-teal disabled:opacity-50"
                aria-label={t('clear_selection')}
              >
                <Icon name={ICONS.CROSS} size="sm" ariaHidden />
              </button>
            </div>
            {/* Row 2: Action buttons */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowBulkDeleteDialog(true)}
                disabled={bulkActionLoading}
                className="flex items-center gap-2 text-xs sm:text-sm justify-center"
              >
                <Icon name={ICONS.DELETE} size="sm" ariaHidden />
                {t('delete')}
              </Button>
              {workflowEnabled && permissions.isCreator && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkSendForReview}
                  disabled={bulkActionLoading}
                  className="flex items-center gap-2 text-xs sm:text-sm justify-center"
                >
                  <Icon name={ICONS.REVIEW} size="sm" ariaHidden />
                  {t('send_for_review', { defaultValue: 'Send for Review' })}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Variations Table */}
        <VariationsTable
          variations={filteredVariations}
          isLoading={isLoading}
          error={error as Error | null}
          onRefresh={handleRefresh}
          onEdit={handleEditVariation}
          onEditPost={handleEditPost}
          getVariationType={getVariationType}
          selectedVariations={selectedVariations}
          onSelect={handleSelectVariation}
          onSelectAll={handleSelectAll}
          onSendForReview={workflowEnabled && permissions.isCreator ? handleSendForReview : undefined}
          workflowEnabled={workflowEnabled}
          canEditVariation={canEditVariation}
          canDeleteVariation={canDeleteVariation}
        />
      </div>

      {/* Filter Drawer */}
      <VariationFilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={{
          search: searchQuery,
          type: typeFilter,
          status: statusFilter,
          language: languageFilter,
          workflowStatus: workflowStatusFilter,
          labels: labelsFilter,
        }}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        workflowEnabled={workflowEnabled}
      />

      {/* VariationCreator Modal */}
      <VariationCreator
        isOpen={isCreatorOpen}
        onClose={handleCloseCreator}
        variation={selectedVariation ?? undefined}
        onSuccess={handleSuccess}
      />

      {/* PostCreator Modal - for editing original posts */}
      <PostCreator
        isOpen={showPostCreator}
        onClose={handleClosePostCreator}
        editingPostId={editingPostId || undefined}
        onSaveSuccess={() => {
          invalidateRelease();
          refetch();
          handleClosePostCreator();
        }}
      />

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        isOpen={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        importType="variations"
        onSuccess={handleRefresh}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialogSimple
        open={showBulkDeleteDialog}
        onOpenChange={(open) => !open && setShowBulkDeleteDialog(false)}
        onConfirm={() => {
          setShowBulkDeleteDialog(false);
          handleBulkDelete();
        }}
        title={t('confirm_bulk_delete', { defaultValue: 'Delete Variations' })}
        description={t('confirm_bulk_delete_description', {
          count: selectedVariations.length,
          defaultValue: `Are you sure you want to delete ${selectedVariations.length} variations? This action cannot be undone.`,
        })}
        confirmText={t('delete')}
        variant="danger"
      />

      {/* Calendar Picker for Mapping Rules */}
      <Modal
        open={showCalendarPicker}
        onOpenChange={(open) => {
          if (!open) {
            setShowCalendarPicker(false);
            setSelectedCalendarId(null);
          }
        }}
        title={t('select_calendar', { defaultValue: 'Select Calendar' })}
        size="sm"
        showCloseButton={false}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('select_calendar_for_mapping', { defaultValue: 'Choose a calendar to manage its mapping rules.' })}
          </p>
          <Select
            value={selectedCalendarId?.toString() || ''}
            onValueChange={(value) => setSelectedCalendarId(parseInt(value, 10))}
            placeholder={t('choose_calendar', { defaultValue: 'Choose calendar...' })}
            options={
              calendars?.map((cal) => ({
                value: cal.id.toString(),
                label: cal.name,
              })) || []
            }
          />
          <div className="flex gap-1.5 sm:gap-2 justify-end pt-2">
            <CancelButton onClick={() => {
              setShowCalendarPicker(false);
              setSelectedCalendarId(null);
            }} />
            <Button
              variant="primary"
              onClick={handleCalendarSelected}
              disabled={!selectedCalendarId}
            >
              {t('manage_rules', { defaultValue: 'Manage Rules' })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Variation Settings Modal */}
      {canManageBrandSettings && (
        <VariationSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
      )}

      {/* Mapping Rules Modal */}
      {selectedCalendarId && calendars && (
        <MappingRulesModal
          isOpen={mappingRulesOpen}
          onClose={() => {
            setMappingRulesOpen(false);
            setSelectedCalendarId(null);
          }}
          calendarId={selectedCalendarId}
          calendarName={
            calendars.find((cal) => cal.id === selectedCalendarId)?.name || 'Calendar'
          }
        />
      )}
    </div>
  );
};
